import React from 'react';

import { MainTemplate } from 'src/templates';
import { Locales } from 'src/lib/constants';
import Footer from 'src/components-v3/Footer';
import Hero from 'src/components-v3/Pricing/Hero/Hero';
import { Spacer } from 'src/components-v2/Layout';
import SandboxBanner from 'src/components-v3/Pricing/SandboxBanner/SandboxBanner';
import ProductTable from 'src/components-v3/Pricing/ProductTable/ProductTable';
import Faq from 'src/components-v3/Faq/Faq';
import { FaqProps } from 'src/components-v3/Faq/Faq.types';
import { Typography } from 'src/components-v2/DataDisplay/Typography';

const metaData = {
  'meta-title': 'Pricing - United States & Canada',
  'meta-description':
    'Build with our free tier; pay as you go; or scale with tailored API solutions and volume pricing. Your first 200 API calls are free.',
  hasForeignContentNotification: true,
  hasFooter: true,
  hasNavigation: true,
  noIndex: false,
};

const Pricing = () => {
  return (
    <MainTemplate
      {...metaData}
      locale={Locales.EN_US}
      cmsTheme='web 3.0'
      hasFooter={false}
      sx={{
        background:
          'var(--gradient-background-subtle-elizabeth, linear-gradient(48deg, #FBF9FF 0%, #F7FAFF 50%, #F2FBFF 100%))',
      }}
    >
      <Hero />
      <Spacer sx={{ height: { xs: '90px' } }} />
      <SandboxBanner />
      <Spacer sx={{ height: { xs: '90px' } }} />
      <ProductTable />
      <Spacer sx={{ height: { xs: '90px' } }} />
      <Faq
        body={
          <>
            <Typography variant='h2' component='h2'>
              Get answers to your pricing questions
            </Typography>
            <Typography
              variant='p'
              component='p'
              sx={{ fontSize: { xs: '1.8rem', md: '2.0rem' } }}
            >
              <a href='/contact/'>Reach out</a> if we missed yours.
            </Typography>
          </>
        }
        faqItems={FAQ_QUESTIONS_AND_ANSWERS}
      />
      <Spacer
        sx={{
          height: {
            xs: '90px',
            sm: '135px',
            md: '180px',
            lg: '225px',
            xl: '270px',
          },
        }}
      />
      <Footer roundedTop overlap />
    </MainTemplate>
  );
};

const FAQ_QUESTIONS_AND_ANSWERS: FaqProps['faqItems'] = [
  {
    question: 'Is Plaid free for consumers?',
    answer:
      'Yes. Consumers can use Plaid to safely connect and control the financial information they share with apps or services at no cost.',
  },
  {
    question: 'Can I try Plaid for free?',
    answer:
      'Absolutely. Our Limited Production service allows you to make up to 200 API calls with each available product using live data. This is the best way to start learning about and testing Plaid’s APIs. ',
  },
  {
    question: 'How much does Plaid cost? ',
    answer: (
      <>
        We offer three basic pricing models based on the product(s) you’re using
        and how it’s designed: one-time, subscription, and per-request.
        <ul>
          <li>
            <strong>One-time fee products</strong>: You’ll incur a charge only
            once per connected account, regardless of the number of API calls
            made.
          </li>
          <li>
            <strong>Subscription products</strong>: These incur a monthly fee
            for each connected account.
          </li>
          <li>
            <strong>Per-request products</strong>: These have a flat fee for
            every successful API call.
          </li>
        </ul>
        For more information on pricing, plans, or volume discounts, please
        visit <a href='/docs/account/billing'>Plaid Billing</a> or connect with
        our <a href='/contact'>sales team</a>.
      </>
    ),
    plainTextAnswer:
      "We offer three basic pricing models based on the product(s) you're using and how it's designed: one-time, subscription, and per-request. One-time fee products: You'll incur a charge only once per connected account, regardless of the number of API calls made. Subscription products: These incur a monthly fee for each connected account. Per-request products: These have a flat fee for every successful API call. For more information on pricing, plans, or volume discounts, please visit Plaid Billing or connect with our sales team.",
  },
  {
    question: 'Does Plaid offer discounts? ',
    answer: (
      <>
        We offer volume discounts based on the length of your contract and/or
        the minimum monthly commitment you choose. For more details, please
        reach out to our <a href='/contact'>sales team</a>.
      </>
    ),
    plainTextAnswer:
      'We offer volume discounts based on the length of your contract and/or the minimum monthly commitment you choose. For more details, please reach out to our sales team.',
  },
  {
    question: 'How does Pay-as-You-Go billing work?',
    answer:
      "It’s simple—once you sign up for the products you need in Plaid's production dashboard, we’ll charge the card on file for the connected items (accounts). No upfront commitments. Only pay for what you use.",
  },
];

export default Pricing;
